import React, { useEffect, useState } from 'react';
import Table from '../../../components/common/table/Table';
import { MdEdit } from 'react-icons/md';
import { FaTrash } from 'react-icons/fa';
import { useAlternateProduct } from '../../../context/alternateProductContext/AlternateProductContextProvider';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import CreateAlternateProduct from './CreateAlternateProduct';
import { useModal } from '../../../components/common/modal/ModalContext';
import ActionButtons from '../../../components/common/ActionButtons';

const AlternateProductTable = () => {
  const {
    alternateProductsList,
    getAlternateProducts,
    deleteAlternateProduct,
    clearFormData,
    totalElements,
    currentPage,
    pageSize,
    setCurrentPage,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    searchQuery,
  } = useAlternateProduct();
  const { openModal, closeModal } = useModal();

  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (id) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === id ? null : id));
  };

  const handleSortChange = ({ field, direction }) => {
    setSortBy(field); // Update the sortBy field
    setSortDir(direction); // Update the sort direction
    getAlternateProducts(currentPage, pageSize, searchQuery, field, direction); // Fetch sorted data
  };

  const columns = [
    { header: 'Product Code', field: 'mainProduct.itemCode' },
    { header: 'Description', field: 'mainProduct.itemDescription' },
    { header: 'Cost', field: 'mainProduct.avgCost' },
    { header: 'Unit', field: 'mainProduct.unit' },
    { header: 'Price0 exGst', field: 'mainProduct.price0exGst' },
    {
      header: 'Alternate Product',
      render: (rowData) => {
        const alternateProductCount = rowData.alternateProducts
          ? rowData?.alternateProducts?.length
          : null;

        return (
          <>
            {alternateProductCount > 0 ? (
              <div className="flex items-center ">
                <div>Alternate Products ({alternateProductCount})</div>
                <button
                  className="text-gray-500 px-2"
                  onClick={() => toggleRow(rowData?.mainProduct?.id)}>
                  <div className="text-xl text-black ">
                    {expandedRow === rowData?.mainProduct?.id ? (
                      <FiChevronUp />
                    ) : (
                      <FiChevronDown />
                    )}
                  </div>
                </button>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      header: 'Action',
      render: (rowData) => (
        <ActionButtons
          actions={[
            {
              icon: <FaTrash />,
              onClick: () => deleteAlternateProduct(rowData?.mainProduct?.id),
              tooltip: 'Delete Alternate Product',
              className: 'text-red-500 pr-5',
              style: { zIndex: 10 }, // Add zIndex to ensure popover is above the table
            },
            {
              icon: <MdEdit />,
              onClick: () => handleOpenModal(rowData),
              tooltip: 'Edit Alternate Product',
              className: 'text-gray-500',
              style: { zIndex: 10 }, // Add zIndex to ensure popover is above the table
            },
          ]}
        />
      ),
    },
  ];

  const handleOpenModal = (user) => {
    clearFormData();
    openModal(
      <CreateAlternateProduct
        closeModal={closeModal}
        data={user}
        isUpdate={true}
      />,
      'Update Alternate Product',
    );
  };

  const renderAlternateProducts = (alternateProducts) => (
    <table className="min-w-full divide-y divide-gray-300 mb-5">
      <thead>
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Alt Product Code
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Alt Description
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Alt Cost
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Alt Unit
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Alt Price0 exGst
          </th>
        </tr>
      </thead>
      <tbody>
        {alternateProducts.map((altProductWrapper) => {
          const altProduct = altProductWrapper?.alternateProduct;
          return (
            <tr
              key={altProduct?.id}
              className="bg-gray-50 border-b font-400 text-sm">
              <td className="px-6 py-1 whitespace-nowrap">
                {altProduct?.itemCode}
              </td>
              <td className="px-6 py-1 whitespace-nowrap">
                {altProduct?.itemDescription}
              </td>
              <td className="px-6 py-1 whitespace-nowrap">
                {altProduct?.avgCost}
              </td>
              <td className="px-6 py-1 whitespace-nowrap">{altProduct.unit}</td>
              <td className="px-6 py-1 whitespace-nowrap">
                {altProduct?.price0exGst}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const dataWithAlternate = (alternateProductsList || []).map((product) => ({
    ...product,
    alternateRow: expandedRow === product?.mainProduct?.id,
    alternateProducts: product?.alternateProducts || [],
  }));

  return (
    <>
      {!alternateProductsList ||
      alternateProductsList.length === 0 ||
      alternateProductsList[0] === 'No data found' ? (
        <div className="text-center text-2xl my-10">No data found</div>
      ) : (
        <Table
          data={dataWithAlternate}
          columns={columns}
          renderRow={(rowData) =>
            rowData?.alternateRow && rowData?.alternateProducts
              ? renderAlternateProducts(rowData?.alternateProducts)
              : null
          }
          totalElements={totalElements}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={(newPage) => setCurrentPage(newPage)}
          onSortChange={handleSortChange} // Pass sorting handler
          sortConfig={{ field: sortBy, direction: sortDir }} // Pass current sorting state
        />
      )}
    </>
  );
};

export default AlternateProductTable;
